import { AuthClientError, ClientErrorCode } from './errors.js';
/**
 * Single request helper every namespace shares. Owns base URL joining, JSON encoding, the
 * `Authorization` header, mapping non-2xx responses into `AuthClientError`, and — when the app
 * passes `refreshAccessToken` — one refresh-and-retry of an `auth: true` call that fails with
 * `401 TOKEN_EXPIRED` (Hangar 7.7).
 */
export function createTransport(config) {
    // Calls that expire together share one refresh: auth-service treats a refresh token presented
    // twice as reuse and revokes the session.
    let refreshing = null;
    function refreshOnce() {
        if (!refreshing) {
            refreshing = Promise.resolve()
                .then(() => config.refreshAccessToken())
                .catch(() => null)
                .finally(() => {
                refreshing = null;
            });
        }
        return refreshing;
    }
    /**
     * The token to retry with after `sentToken` came back expired. If another call already
     * refreshed while this one was in flight, the current token is newer than the one sent:
     * use it rather than refreshing again.
     */
    async function freshToken(sentToken) {
        const current = config.getAccessToken?.() ?? null;
        if (current && current !== sentToken) {
            return current;
        }
        return refreshOnce();
    }
    return async function request(path, options = {}) {
        const { method = 'GET', body, auth = false, signal } = options;
        async function send(token) {
            const headers = {};
            if (body !== undefined) {
                headers['Content-Type'] = 'application/json';
            }
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            try {
                return await config.fetch(`${config.baseUrl}${path}`, {
                    method,
                    headers,
                    body: body !== undefined ? JSON.stringify(body) : undefined,
                    signal,
                });
            }
            catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') {
                    throw err;
                }
                throw new AuthClientError(ClientErrorCode.NETWORK_ERROR, err instanceof Error ? err.message : 'Network request failed', 0);
            }
        }
        let token = null;
        if (auth) {
            token = config.getAccessToken?.() ?? null;
            if (!token) {
                throw new AuthClientError('MISSING_ACCESS_TOKEN', 'This call requires getAccessToken to return a token', 0);
            }
        }
        let response = await send(token);
        // Retrying is safe even for POSTs: auth-service's authenticate middleware rejects an expired
        // token before the route handler runs, so the first attempt changed nothing.
        if (token && config.refreshAccessToken && response.status === 401) {
            const error = await toError(response);
            if (error.code !== 'TOKEN_EXPIRED') {
                throw error;
            }
            const retryToken = await freshToken(token);
            if (!retryToken) {
                throw error;
            }
            response = await send(retryToken);
        }
        if (!response.ok) {
            throw await toError(response);
        }
        if (response.status === 204) {
            return undefined;
        }
        return (await response.json());
    };
}
async function toError(response) {
    let parsed;
    try {
        parsed = (await response.json());
    }
    catch {
        // Body wasn't JSON — fall through with a generic error below.
    }
    return new AuthClientError(parsed?.error.code ?? 'UNKNOWN_ERROR', parsed?.error.message ?? response.statusText, response.status, parsed?.error.details);
}
